import { Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import { ESTIMATE_STATUS, normalizeWorkOrderStatus, WORK_ORDER_STATUS } from '../utility/Constant';
import { CustomTooltip } from '../utility/helpers/commonFunction';
import LinkIcon from '../utility/Svg/LinkIcon';
import InfoIconSvg from '../utility/Svg/InfoIconSvg';

const LINK_TEXT_SX = {
    color: '#002394',
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
    letterSpacing: '0.15px',
    '&:hover': {
        textDecoration: 'underline',
    },
};

/**
 * "Linked to <document number>" — the one way a linked estimate, invoice or work order is shown,
 * on detail and edit pages alike. Always reads "Linked to", never "Created from", whichever
 * direction the link was made in.
 *
 * @param {string} to            route of the linked document
 * @param {string} documentNumber number of the linked document, e.g. WO26-1000
 * @param {object} [syncPrompt]   when the linked document is out of date: `{ message, actionLabel, onAction }`.
 *                                Hovering the link then shows the same tooltip + button the estimate uses
 *                                for "Estimate changes have not been updated on the invoice." (spec §6.5)
 */
const LinkedDocumentLink = ({ to, documentNumber, syncPrompt }) => {
    if (!documentNumber) return null;

    const link = (
        <Link to={to || '#'} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <LinkIcon color="#002394" />
            <Typography variant="body1" component="span" sx={LINK_TEXT_SX}>
                Linked to {documentNumber}
            </Typography>
        </Link>
    );

    if (!syncPrompt) return link;

    return (
        <Stack direction="row" spacing={0.5}>
            {link}
            <CustomTooltip
                width="245px"
                // The link sits at the right edge of the page header: open below it, right-aligned with the
                // link, so the tooltip never runs off the screen.
                placement="bottom-end"
                leaveDelay={300}
                interactive
                title={
                    <>
                        <Typography color={'#212121'} mb={'11px'} fontSize={'14px'} fontWeight={400} lineHeight={'140%'} letterSpacing={'-0.42px'} align="left">
                            {syncPrompt.message}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            fullWidth
                            sx={{
                                height: '28px',
                                fontSize: '12px',
                                fontWeight: 500,
                                letterSpacing: '0.46px',
                                letterSpacing: '0.46px',
                            }}
                            style={{ textAlign: 'left' }}
                            onClick={syncPrompt.onAction}
                        >
                            {syncPrompt.actionLabel}
                        </Button>
                    </>
                }
            >
                <Box sx={{ display: 'inline-flex', cursor: 'pointer', alignItems: 'center' }}>
                    <InfoIconSvg />
                </Box>
            </CustomTooltip>
        </Stack>
    );
};

export default LinkedDocumentLink;

/**
 * Link lists come back as an array, a single object, or nothing. Entries without an `_id` (a
 * deleted invoice or work order the backend has not unlinked yet) are dropped, so a stale link
 * never breaks a detail page.
 */
export const asLinkedList = (value) => (Array.isArray(value) ? value : value ? [value] : []).filter((item) => Boolean(item?._id));

/** The invoices linked to an estimate. */
export const estimateInvoices = (estimate) => asLinkedList(estimate?.linkedInvoiceId);

/** First linked document — the API returns links as an array or a single object. */
export const firstLinked = (value) => asLinkedList(value)[0];

/** Invoice detail route by status — the mapping every detail page already uses. */
export const getInvoiceDetailUrl = (invoice) => {
    if (!invoice?._id) return '#';
    let urlStatus;
    if (invoice.status === 'draft') urlStatus = 'draft';
    else if (invoice.status === 'sent') urlStatus = invoice.isEmailSent ? 'sent' : 'finalized';
    else urlStatus = invoice.status || 'finalized';
    return `/invoice/${urlStatus}/${invoice._id}`;
};

/** Estimate detail route by status. */
export const getEstimateDetailUrl = (estimate) => {
    if (!estimate?._id) return '#';
    let urlStatus = 'finalized';
    if (estimate.status === ESTIMATE_STATUS.DRAFT) urlStatus = 'draft';
    else if (estimate.status === ESTIMATE_STATUS.INVOICED) urlStatus = 'invoiced';
    else if (estimate.status === ESTIMATE_STATUS.WORK_ORDER) urlStatus = 'work-order';
    else if (estimate.status === ESTIMATE_STATUS.SENT) urlStatus = estimate.isEmailSent ? 'sent' : 'finalized';
    return `/estimates/${urlStatus}/${estimate._id}`;
};

/** The estimate list an estimate belongs to — the middle breadcrumb on its detail page. */
export const getEstimateListCrumb = (estimate) => {
    if (estimate?.status === ESTIMATE_STATUS.INVOICED) return { label: 'Invoiced', to: '/estimates/invoiced' };
    if (estimate?.status === ESTIMATE_STATUS.WORK_ORDER) return { label: 'Work Order', to: '/estimates/work-orders' };
    if (estimate?.status === ESTIMATE_STATUS.DRAFT) return { label: 'Drafts', to: '/estimates/drafts' };
    return estimate?.isEmailSent ? { label: 'Sent', to: '/estimates/sents' } : { label: 'Finalized', to: '/estimates/finalized' };
};

/** Work order detail route by status; the API spells statuses its own way, so normalise first. */
export const getWorkOrderDetailUrl = (workOrder) => {
    if (!workOrder?._id) return '#';
    return `/work-orders/${normalizeWorkOrderStatus(workOrder.status) || WORK_ORDER_STATUS.OPEN}/${workOrder._id}`;
};

/*
 * "Update to …" sync between linked documents (spec §6.5). The backend flips the flag on the target
 * to false when its source changes, and back to true when the target is saved from the sync route.
 * Strict `=== false`, like the estimate's Update To Invoice menu item — a record without the flag is
 * not treated as out of date.
 */

/** Work order ⇒ Invoice: the work order's invoice when it has fallen behind the work order. */
export const workOrderInvoiceToSync = (workOrder) => {
    const invoice = firstLinked(workOrder?.linkedInvoiceId);
    return invoice?._id && invoice.isSyncedWithWOA === false ? invoice : null;
};

/** Estimate ⇒ Work order: the estimate's work order when it has fallen behind the estimate. */
export const estimateWorkOrderToSync = (estimate) => {
    const workOrder = firstLinked(estimate?.linkedWorkOrderId);
    return workOrder?._id && workOrder.isSyncedWithEstimate === false ? workOrder : null;
};

/** Invoice editor, refreshed from its work order. */
export const getInvoiceSyncFromWorkOrderUrl = (invoiceId) => `/invoice/edit-invoice-from-work-order/${invoiceId}`;

/** Work order editor, refreshed from its estimate. */
export const getWorkOrderSyncFromEstimateUrl = (workOrderId) => `/work-orders/edit-from-estimate/${workOrderId}`;

export const SYNC_PROMPT_TEXT = {
    WORK_ORDER_TO_INVOICE: 'Work order changes have not been updated to the invoice.',
    ESTIMATE_TO_WORK_ORDER: 'Estimate changes have not been updated to the work order.',
};
